/* eslint-disable @typescript-eslint/no-use-before-define */
// Abstract base ↔ concrete subclass references below are intentional and safe
// under class hoisting — every reference site runs after both classes are fully
// initialized.
import { case as case_, int32, union, void as voidType } from "@stellar/js-xdr";
import { XdrError, type XdrType } from "@stellar/js-xdr";
import { XdrValue } from "../values/xdr-value.js";

export type LedgerHeaderHistoryEntryExtWire = { v: 0 };

export type LedgerHeaderHistoryEntryExtVariantName = "v0";

/**
 * ```xdr
 * union switch (int v)
 *     {
 *     case 0:
 *         void;
 *     }
 * ```
 */
abstract class LedgerHeaderHistoryEntryExtBase extends XdrValue {
  abstract readonly type: LedgerHeaderHistoryEntryExtVariantName;

  constructor() {
    super();
    // `new.target`, not an unconditional throw: every arm subclass reaches
    // this constructor through `super()`, void arms via an implicit one
    if (new.target === LedgerHeaderHistoryEntryExtBase) {
      throw new TypeError(
        "new xdr.LedgerHeaderHistoryEntryExt(...) is not supported: XDR unions are built from " +
          "per-variant factories. Call xdr.LedgerHeaderHistoryEntryExt.v0() " +
          "(or another arm factory) instead.",
      );
    }
  }

  static readonly schema: XdrType<LedgerHeaderHistoryEntryExtWire> = union(
    "LedgerHeaderHistoryEntryExt",
    {
      switchOn: int32(),
      cases: [case_("v0", 0, voidType())],
      switchKey: "v",
    },
  );

  static v0(): LedgerHeaderHistoryEntryExtV0 {
    return new LedgerHeaderHistoryEntryExtV0();
  }

  static fromXdrObject(
    wire: LedgerHeaderHistoryEntryExtWire,
  ): LedgerHeaderHistoryEntryExt {
    switch (wire.v) {
      case 0:
        return new LedgerHeaderHistoryEntryExtV0();
    }
    // unreachable for a well-typed wire object; a hand-built one can still
    // carry an out-of-range discriminant
    throw new XdrError(
      `LedgerHeaderHistoryEntryExt: unknown v ${(wire as { v: unknown }).v}`,
    );
  }

  /**
   * Type guard narrowing an unknown value to a concrete LedgerHeaderHistoryEntryExt variant.
   * Use this instead of `instanceof LedgerHeaderHistoryEntryExt`: the exported `LedgerHeaderHistoryEntryExt` value
   * is the abstract base, so `instanceof` narrows to the base (not the
   * variant union) and forces a cast. `LedgerHeaderHistoryEntryExt.is(x)` narrows to the union.
   */
  static is(value: unknown): value is LedgerHeaderHistoryEntryExt {
    return value instanceof LedgerHeaderHistoryEntryExtBase;
  }

  abstract toXdrObject(): LedgerHeaderHistoryEntryExtWire;
}

export class LedgerHeaderHistoryEntryExtV0 extends LedgerHeaderHistoryEntryExtBase {
  readonly type = "v0" as const;

  get value(): null {
    return null;
  }

  toXdrObject(): Extract<LedgerHeaderHistoryEntryExtWire, { v: 0 }> {
    return { v: 0 };
  }
}

export type LedgerHeaderHistoryEntryExt = LedgerHeaderHistoryEntryExtV0;
export const LedgerHeaderHistoryEntryExt = LedgerHeaderHistoryEntryExtBase;
